/**
 * Contratos dos cadastros (Fase 2). O mesmo schema valida o formulário no
 * frontend e a requisição na API. O banco repete as regras essenciais (CHECKs).
 */
import { z } from "zod";
import { UF_LIST } from "./contracts";
import { decimalPlaces, formatDecimalBR, parseDecimalBR } from "./decimal-input";
import { isValidCnpj, isValidCpf, isValidGtin, onlyDigits } from "./documents";

// ---------------------------------------------------------------- blocos comuns
const text = (min: number, max: number, msg: string) => z.string().trim().min(min, msg).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional()
    .transform((v) => v ?? null);
const optionalDigits = (len: number, label: string) =>
  z
    .string()
    .transform(onlyDigits)
    .refine((v) => v === "" || v.length === len, `${label} deve ter ${len} dígitos`)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional()
    .transform((v) => v ?? null);
const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .refine((v) => v === "" || z.email().safeParse(v).success, "E-mail inválido")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional()
  .transform((v) => v ?? null);
const optionalUuid = z
  .union([z.uuid(), z.literal("")])
  .nullable()
  .optional()
  .transform((v) => (v ? v : null));

/**
 * Decimal digitado no padrão brasileiro ("10.000,00", "12,5"), com ponto ("12.5") ou número,
 * com no máximo `scale` casas; devolve texto com ponto (formato do banco). Com 2 casas (valores
 * em reais) o ponto sem vírgula em grupos de 3 é milhar ("10.000" = dez mil); nos demais
 * (quantidade, fator, preço com 4 casas) o ponto continua decimal, como sempre foi.
 */
export const decimalSchema = (scale: number, opts: { min?: number; max?: number } = {}) =>
  z
    .union([z.string(), z.number()])
    .transform((v, ctx) => {
      const canonical = parseDecimalBR(v, scale === 2 ? "dinheiro" : "numero");
      if (canonical === null) {
        ctx.addIssue({
          code: "custom",
          message:
            scale === 2 ? "Informe um valor, ex.: 10.000,00." : "Informe um número, ex.: 1,5.",
        });
        return z.NEVER;
      }
      return canonical;
    })
    .refine((v) => decimalPlaces(v) <= scale, {
      message: `Use no máximo ${scale} casas decimais.`,
    })
    .refine((v) => opts.min === undefined || Number(v) >= opts.min, {
      message: `O valor mínimo é ${formatDecimalBR(opts.min ?? 0)}.`,
    })
    .refine((v) => opts.max === undefined || Number(v) <= opts.max, {
      message: `O valor máximo é ${formatDecimalBR(opts.max ?? 0)}.`,
    });
const optionalDecimal = (scale: number, opts: { min?: number; max?: number } = {}) =>
  z
    .union([decimalSchema(scale, opts), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v === "" || v === undefined ? null : v));

/**
 * Código do cadastro: vazio, null ou ausente = gerado automaticamente ao salvar.
 * Maiúsculas, sem espaços. Aceita null porque a tela envia o próprio resultado desta
 * validação (vazio vira null) e a API valida de novo.
 */
export const cadastroCodeSchema = z
  .union([
    z
      .string()
      .trim()
      .toUpperCase()
      .refine(
        (v) => v === "" || /^[A-Z0-9][A-Z0-9._/-]{0,29}$/.test(v),
        "Código: até 30 letras, números ou . _ / -",
      ),
    z.null(),
  ])
  .optional()
  .transform((v) => (v ? v : null));

export const STATUS_LIST = ["active", "inactive"] as const;
export const statusSchema = z.enum(STATUS_LIST).default("active");
export const STATUS_LABEL: Record<(typeof STATUS_LIST)[number], string> = {
  active: "Ativo",
  inactive: "Inativo",
};

const gtinSchema = z
  .string()
  .trim()
  .refine((v) => v === "" || isValidGtin(v), "GTIN/EAN inválido")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional()
  .transform((v) => v ?? null);

/** Trava otimista: toda alteração envia a versão que o usuário viu. */
export const versionSchema = z.object({ version: z.number().int().min(1) });

export const listQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(["active", "inactive", "all"]).default("active"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListQuery = z.input<typeof listQuerySchema>;

/** Cadastros com código automático (prévia do próximo código ao pressionar Enter). */
export const CODE_KINDS = ["cliente", "fornecedor", "produto", "vendedor", "deposito"] as const;
export type CodeKind = (typeof CODE_KINDS)[number];
export const nextCodeQuerySchema = z.object({
  tipo: z.enum(CODE_KINDS, "Tipo de cadastro inválido"),
});
/** Prévia: o código que o próximo cadastro deve receber. Não reserva nada. */
export type NextCodePreview = { tipo: CodeKind; code: string };

export type Page<T> = { items: T[]; total: number; limit: number; offset: number };

// ---------------------------------------------------------------- unidades
export const unitInputSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{1,6}$/, "Código: até 6 letras ou números"),
  name: text(1, 60, "Informe o nome"),
  decimals: z.coerce.number().int().min(0).max(6).default(0),
  status: statusSchema,
});
export const unitUpdateSchema = unitInputSchema.omit({ code: true }).extend(versionSchema.shape);
export type UnitInput = z.input<typeof unitInputSchema>;

export type Unit = {
  id: string;
  code: string;
  name: string;
  decimals: number;
  status: "active" | "inactive";
  version: number;
};

// ---------------------------------------------------------------- formas de pagamento
export const PAYMENT_KINDS = [
  "dinheiro",
  "pix",
  "boleto",
  "cartao_credito",
  "cartao_debito",
  "cheque",
  "transferencia",
  "prazo",
  "outros",
] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];
export const PAYMENT_KIND_LABEL: Record<PaymentKind, string> = {
  dinheiro: "Dinheiro",
  pix: "PIX",
  boleto: "Boleto",
  cartao_credito: "Cartão de crédito",
  cartao_debito: "Cartão de débito",
  cheque: "Cheque",
  transferencia: "Transferência",
  prazo: "A prazo (crediário)",
  outros: "Outros",
};

/** Códigos tPag da NF-e 4.00. */
export const NFE_PAYMENT_CODES = {
  "01": "Dinheiro",
  "02": "Cheque",
  "03": "Cartão de crédito",
  "04": "Cartão de débito",
  "05": "Crédito loja",
  "10": "Vale alimentação",
  "11": "Vale refeição",
  "12": "Vale presente",
  "13": "Vale combustível",
  "15": "Boleto bancário",
  "16": "Depósito bancário",
  "17": "PIX",
  "18": "Transferência / carteira digital",
  "19": "Fidelidade / cashback",
  "90": "Sem pagamento",
  "99": "Outros",
} as const;
export type NfePaymentCode = keyof typeof NFE_PAYMENT_CODES;
export const NFE_PAYMENT_CODE_LIST = Object.keys(NFE_PAYMENT_CODES) as [
  NfePaymentCode,
  ...NfePaymentCode[],
];

/** tPag sugerido para cada tipo (o usuário pode trocar). */
export const PAYMENT_KIND_DEFAULT_TPAG: Record<PaymentKind, NfePaymentCode> = {
  dinheiro: "01",
  pix: "17",
  boleto: "15",
  cartao_credito: "03",
  cartao_debito: "04",
  cheque: "02",
  transferencia: "18",
  prazo: "05",
  outros: "99",
};

export const paymentMethodInputSchema = z
  .object({
    name: text(2, 60, "Informe o nome"),
    kind: z.enum(PAYMENT_KINDS),
    nfePaymentCode: z.enum(NFE_PAYMENT_CODE_LIST),
    installments: z.coerce.number().int().min(1).max(60).default(1),
    firstDueDays: z.coerce.number().int().min(0).max(365).default(0),
    intervalDays: z.coerce.number().int().min(0).max(365).default(30),
    generatesReceivable: z.boolean().default(false),
    status: statusSchema,
  })
  .refine((v) => v.installments === 1 || v.generatesReceivable, {
    message: "Forma de pagamento parcelada precisa gerar contas a receber.",
    path: ["generatesReceivable"],
  });
export const paymentMethodUpdateSchema = paymentMethodInputSchema.and(versionSchema);
export type PaymentMethodInput = z.input<typeof paymentMethodInputSchema>;

export type PaymentMethod = {
  id: string;
  name: string;
  kind: PaymentKind;
  nfePaymentCode: NfePaymentCode;
  installments: number;
  firstDueDays: number;
  intervalDays: number;
  generatesReceivable: boolean;
  status: "active" | "inactive";
  version: number;
};

// ---------------------------------------------------------------- tabelas de preço
const isoDate = z
  .union([z.iso.date("Data inválida"), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v ? v : null));

export const priceListInputSchema = z
  .object({
    name: text(2, 80, "Informe o nome"),
    description: optionalText(300),
    validFrom: isoDate,
    validUntil: isoDate,
    isDefault: z.boolean().default(false),
    status: statusSchema,
  })
  .refine((v) => !v.validFrom || !v.validUntil || v.validUntil >= v.validFrom, {
    message: "A validade final não pode ser anterior à inicial.",
    path: ["validUntil"],
  })
  .refine((v) => !v.isDefault || v.status === "active", {
    message: "A tabela padrão precisa estar ativa.",
    path: ["status"],
  });
export const priceListUpdateSchema = priceListInputSchema.and(versionSchema);
export type PriceListInput = z.input<typeof priceListInputSchema>;

export type PriceList = {
  id: string;
  name: string;
  description: string | null;
  validFrom: string | null;
  validUntil: string | null;
  isDefault: boolean;
  status: "active" | "inactive";
  version: number;
  itemCount: number;
};

export const priceListItemInputSchema = z.object({
  productId: z.uuid("Selecione o produto"),
  unitId: z.uuid("Selecione a unidade"),
  price: decimalSchema(4, { min: 0 }),
  maxDiscountPercent: optionalDecimal(2, { min: 0, max: 100 }),
});
export type PriceListItemInput = z.input<typeof priceListItemInputSchema>;

export type PriceListItem = {
  id: string;
  productId: string;
  productCode: string;
  productName: string;
  unitId: string;
  unitCode: string;
  /** unidades-base por unidade do preço */
  factor: string;
  baseUnitCode: string;
  price: string;
  maxDiscountPercent: string | null;
  version: number;
};

// ---------------------------------------------------------------- vendedores
export const salespersonInputSchema = z.object({
  code: cadastroCodeSchema,
  name: text(2, 120, "Informe o nome"),
  document: z
    .string()
    .transform(onlyDigits)
    .refine((v) => v === "" || isValidCpf(v), "CPF inválido")
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  email: optionalEmail,
  phone: optionalText(30),
  userId: optionalUuid,
  commissionPercent: decimalSchema(2, { min: 0, max: 100 }).default("0"),
  notes: optionalText(2000),
  status: statusSchema,
});
export const salespersonUpdateSchema = salespersonInputSchema.extend(versionSchema.shape);
export type SalespersonInput = z.input<typeof salespersonInputSchema>;

export type Salesperson = {
  id: string;
  code: string;
  name: string;
  document: string | null;
  email: string | null;
  phone: string | null;
  userId: string | null;
  userEmail: string | null;
  commissionPercent: string;
  notes: string | null;
  status: "active" | "inactive";
  version: number;
};

// ---------------------------------------------------------------- produtos e embalagens
export const PRODUCT_TYPES = [
  "produto_acabado",
  "materia_prima",
  "embalagem",
  "insumo",
  "revenda",
] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];
export const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  produto_acabado: "Produto acabado",
  materia_prima: "Matéria-prima (ex.: café cru)",
  embalagem: "Embalagem / etiqueta",
  insumo: "Insumo",
  revenda: "Mercadoria para revenda",
};

/** Origem da mercadoria (tabela A do ICMS). */
export const PRODUCT_ORIGINS = {
  "0": "0 — Nacional",
  "1": "1 — Estrangeira, importação direta",
  "2": "2 — Estrangeira, adquirida no mercado interno",
  "3": "3 — Nacional, conteúdo de importação > 40% e ≤ 70%",
  "4": "4 — Nacional, processos produtivos básicos",
  "5": "5 — Nacional, conteúdo de importação ≤ 40%",
  "6": "6 — Estrangeira, importação direta, sem similar (CAMEX)",
  "7": "7 — Estrangeira, mercado interno, sem similar (CAMEX)",
  "8": "8 — Nacional, conteúdo de importação > 70%",
} as const;
export type ProductOrigin = keyof typeof PRODUCT_ORIGINS;
const ORIGIN_LIST = Object.keys(PRODUCT_ORIGINS) as [ProductOrigin, ...ProductOrigin[]];

/**
 * Embalagem: informe o FATOR direto (unidades-base por embalagem)
 * OU a composição ("contém 10 PCT"), e o banco calcula o fator.
 */
export const packagingInputSchema = z
  .object({
    unitId: z.uuid("Selecione a unidade da embalagem"),
    factor: optionalDecimal(6, { min: 0 }),
    containsQuantity: optionalDecimal(6, { min: 0 }),
    containsUnitId: optionalUuid,
    gtin: gtinSchema,
    description: optionalText(120),
    isSaleUnit: z.boolean().default(true),
    isPurchaseUnit: z.boolean().default(false),
    status: statusSchema,
  })
  .refine(
    (v) =>
      (v.containsQuantity !== null &&
        v.containsUnitId !== null &&
        Number(v.containsQuantity) > 0) ||
      (v.containsQuantity === null &&
        v.containsUnitId === null &&
        v.factor !== null &&
        Number(v.factor) > 0),
    {
      message: "Informe o fator (maior que zero) ou a composição completa (quantidade e unidade).",
      path: ["factor"],
    },
  )
  .refine((v) => v.containsUnitId === null || v.containsUnitId !== v.unitId, {
    message: "A embalagem não pode conter ela mesma.",
    path: ["containsUnitId"],
  });
export const packagingUpdateSchema = packagingInputSchema.and(versionSchema);
export type PackagingInput = z.input<typeof packagingInputSchema>;

const productFields = {
  code: cadastroCodeSchema,
  name: text(2, 120, "Informe o nome"),
  description: optionalText(2000),
  type: z.enum(PRODUCT_TYPES).default("produto_acabado"),
  ncm: optionalDigits(8, "NCM"),
  cest: optionalDigits(7, "CEST"),
  origin: z.enum(ORIGIN_LIST).default("0"),
  gtin: gtinSchema,
  netWeightKg: optionalDecimal(4, { min: 0 }),
  grossWeightKg: optionalDecimal(4, { min: 0 }),
  controlsLot: z.boolean().default(false),
  /** v7: rastreabilidade obrigatória (legal, fiscal ou sanitária): nunca vendido sem estoque */
  strictTraceability: z.boolean().default(false),
  shelfLifeDays: z
    .union([z.coerce.number().int().min(1).max(3650), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v === "" || v === undefined ? null : v)),
  status: statusSchema,
  /** Fase 7 — exceção da TIPI e grupo tributário (a tributação fica na matriz) */
  exTipi: z
    .union([z.string().regex(/^\d{1,3}$/, "EX TIPI com até 3 dígitos"), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v ? v : null)),
  taxGroupId: optionalUuid,
};

export const productCreateSchema = z.object({
  ...productFields,
  baseUnitId: z.uuid("Selecione a unidade-base"),
  packagings: z.array(packagingInputSchema).max(10).default([]),
});
export const productUpdateSchema = z.object({ ...productFields, ...versionSchema.shape });
export type ProductCreateInput = z.input<typeof productCreateSchema>;

export type Packaging = {
  id: string;
  unitId: string;
  unitCode: string;
  unitName: string;
  factor: string;
  containsQuantity: string | null;
  containsUnitId: string | null;
  containsUnitCode: string | null;
  gtin: string | null;
  description: string | null;
  isSaleUnit: boolean;
  isPurchaseUnit: boolean;
  status: "active" | "inactive";
  version: number;
};

export type Product = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  type: ProductType;
  baseUnitId: string;
  baseUnitCode: string;
  ncm: string | null;
  cest: string | null;
  origin: ProductOrigin;
  gtin: string | null;
  netWeightKg: string | null;
  grossWeightKg: string | null;
  controlsLot: boolean;
  /** v7: rastreabilidade obrigatória — nunca vendido sem estoque */
  strictTraceability: boolean;
  shelfLifeDays: number | null;
  status: "active" | "inactive";
  version: number;
  exTipi: string | null;
  taxGroupId: string | null;
  packagings?: Packaging[];
};

/** Linha da lista/pesquisa de produtos (com os extras da lupa da venda, quando pedidos). */
export type ProductListRow = Product & {
  packagingCount: number;
  /** preço da tabela do cliente na unidade-base (só com `customerId`) */
  listPrice: string | null;
  /** disponível no depósito, unidade-base (só com `warehouseId`) */
  available?: string;
};

// ---------------------------------------------------------------- clientes e fornecedores
export const IE_INDICATORS = {
  "1": "Contribuinte de ICMS",
  "2": "Contribuinte isento de inscrição",
  "9": "Não contribuinte",
} as const;
type IeIndicator = keyof typeof IE_INDICATORS;

const partyObject = z.object({
  code: cadastroCodeSchema,
  kind: z.enum(["pf", "pj"]),
  name: text(2, 160, "Informe o nome / razão social"),
  tradeName: optionalText(160),
  document: z
    .string()
    .transform(onlyDigits)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null)),
  stateRegistration: optionalText(30),
  ieIndicator: z.enum(Object.keys(IE_INDICATORS) as [IeIndicator, ...IeIndicator[]]).default("9"),
  municipalRegistration: optionalText(30),
  email: optionalEmail,
  phone: optionalText(30),
  mobile: optionalText(30),
  cep: optionalDigits(8, "CEP"),
  street: optionalText(160),
  number: optionalText(20),
  complement: optionalText(80),
  district: optionalText(80),
  city: optionalText(80),
  cityIbgeCode: optionalDigits(7, "Código IBGE"),
  uf: z
    .union([z.enum([...UF_LIST, "EX"] as const), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v ? v : null)),
  isCustomer: z.boolean().default(false),
  isSupplier: z.boolean().default(false),
  creditLimit: optionalDecimal(2, { min: 0 }),
  salespersonId: optionalUuid,
  priceListId: optionalUuid,
  paymentMethodId: optionalUuid,
  notes: optionalText(2000),
  status: statusSchema,
  /** Fase 7 — dados fiscais do destinatário (sem tributação): null = não informado */
  finalConsumer: z.boolean().nullable().default(null),
  ruralProducer: z.boolean().default(false),
});

type PartyShape = z.output<typeof partyObject>;
function partyRules(v: PartyShape, ctx: z.RefinementCtx) {
  if (!v.isCustomer && !v.isSupplier) {
    ctx.addIssue({
      code: "custom",
      message: "Marque se é cliente, fornecedor ou ambos.",
      path: ["isCustomer"],
    });
  }
  if (v.document) {
    const ok = v.kind === "pf" ? isValidCpf(v.document) : isValidCnpj(v.document);
    if (!ok) {
      ctx.addIssue({
        code: "custom",
        message: v.kind === "pf" ? "CPF inválido" : "CNPJ inválido",
        path: ["document"],
      });
    }
  }
  if (v.ieIndicator === "1" && !v.stateRegistration) {
    ctx.addIssue({
      code: "custom",
      message: "Contribuinte de ICMS precisa de inscrição estadual.",
      path: ["stateRegistration"],
    });
  }
}

export const partyInputSchema = partyObject.superRefine(partyRules);
export const partyUpdateSchema = partyObject.extend(versionSchema.shape).superRefine(partyRules);
export type PartyInput = z.input<typeof partyInputSchema>;

export const partyListQuerySchema = listQuerySchema.extend({
  papel: z.enum(["cliente", "fornecedor", "todos"]).default("todos"),
  /** filtro da lupa */
  uf: z.preprocess(
    (v) => (v === "" ? undefined : v),
    z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/, "UF inválida")
      .optional(),
  ),
});

/**
 * Cadastro RÁPIDO de cliente durante a venda na rota (permissão própria
 * `cadastros.clientes.cadastrar_rota`): só identificação, contato, endereço e dados fiscais.
 * Nada administrativo — limite de crédito, tabela de preço, vendedor, forma de pagamento e
 * situação são definidos pelo servidor (o cliente nasce com limite de crédito 0: compra à vista
 * até o escritório definir o limite). Campos a mais são recusados (`strict`).
 */
export const partyQuickSchema = partyObject
  .pick({
    kind: true,
    name: true,
    tradeName: true,
    document: true,
    stateRegistration: true,
    ieIndicator: true,
    email: true,
    phone: true,
    mobile: true,
    cep: true,
    street: true,
    number: true,
    complement: true,
    district: true,
    city: true,
    cityIbgeCode: true,
    uf: true,
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.document) {
      const ok = v.kind === "pf" ? isValidCpf(v.document) : isValidCnpj(v.document);
      if (!ok) {
        ctx.addIssue({
          code: "custom",
          message: v.kind === "pf" ? "CPF inválido" : "CNPJ inválido",
          path: ["document"],
        });
      }
    }
    if (v.ieIndicator === "1" && !v.stateRegistration) {
      ctx.addIssue({
        code: "custom",
        message: "Contribuinte de ICMS precisa de inscrição estadual.",
        path: ["stateRegistration"],
      });
    }
    if (!v.phone && !v.mobile && !v.document) {
      ctx.addIssue({
        code: "custom",
        message: "Informe o CPF/CNPJ ou um telefone (para achar o cliente depois).",
        path: ["phone"],
      });
    }
  });
export type PartyQuickInput = z.input<typeof partyQuickSchema>;

export type Party = z.output<typeof partyObject> & {
  id: string;
  code: string;
  version: number;
  salespersonName?: string | null;
};

// ---------------------------------------------------------------- opções para formulários
export type CadastroOptions = {
  units: Pick<Unit, "id" | "code" | "name" | "decimals">[];
  paymentMethods: Pick<PaymentMethod, "id" | "name" | "kind">[];
  priceLists: Pick<PriceList, "id" | "name" | "isDefault">[];
  salespeople: Pick<Salesperson, "id" | "code" | "name">[];
  members: { userId: string; email: string; fullName: string | null }[];
};
